/**
 * Cache Performance Monitor
 * Tracks cache performance metrics and optionally exports to Prometheus
 */

export interface CacheMetrics {
  hitRate: number;
  missRate: number;
  averageLoadTime: number;
  totalCacheSize: number;
  networkBytesSaved: number;
  assetsServedFromCache: number;
  totalRequests: number;
  cacheHits: number;
  cacheMisses: number;
  serviceWorkerErrors: number;
}

export interface CacheEvent {
  type: 'hit' | 'miss' | 'error' | 'eviction' | 'preload';
  url: string;
  loadTime?: number;
  size?: number;
  reason?: string;
  timestamp: number;
}

export interface PrometheusMetrics {
  cache_hits_total: number;
  cache_misses_total: number;
  cache_requests_total: number;
  cache_hit_ratio: number;
  cache_load_time_seconds: number;
  cache_size_bytes: number;
  cache_evictions_total: number;
  service_worker_errors_total: number;
}

class CachePerformanceMonitor {
  private metrics: CacheMetrics;
  private events: CacheEvent[] = [];
  private maxEvents = 1000; // Keep last 1000 events
  private prometheusEnabled = false;

  constructor() {
    this.metrics = {
      hitRate: 0,
      missRate: 0,
      averageLoadTime: 0,
      totalCacheSize: 0,
      networkBytesSaved: 0,
      assetsServedFromCache: 0,
      totalRequests: 0,
      cacheHits: 0,
      cacheMisses: 0,
      serviceWorkerErrors: 0
    };

    // Check if we should enable Prometheus metrics
    this.checkPrometheusSupport();
  }

  /**
   * Track a cache hit
   */
  trackCacheHit(url: string, loadTime: number, size?: number): void {
    const event: CacheEvent = {
      type: 'hit',
      url,
      loadTime,
      size,
      timestamp: Date.now()
    };

    this.addEvent(event);
    this.metrics.cacheHits++;
    this.metrics.totalRequests++;
    this.metrics.assetsServedFromCache++;

    if (size) {
      this.metrics.networkBytesSaved += size;
    }

    this.updateRates();
    this.updateAverageLoadTime(loadTime);

    // console.log(`[CACHE] Hit: ${url} (${loadTime}ms)`);
    
    this.exportToPrometheus();
  }

  /**
   * Track a cache miss
   */
  trackCacheMiss(url: string, reason: string, loadTime?: number): void {
    const event: CacheEvent = {
      type: 'miss',
      url,
      reason,
      loadTime,
      timestamp: Date.now()
    };

    this.addEvent(event);
    this.metrics.cacheMisses++;
    this.metrics.totalRequests++;

    if (loadTime) {
      this.updateAverageLoadTime(loadTime);
    }

    this.updateRates();

    // console.log(`[CACHE] Miss: ${url} (${reason})`);
    
    this.exportToPrometheus();
  }

  /**
   * Track a service worker event
   */
  trackServiceWorkerEvent(event: string, data: any): void {
    // console.log(`[CACHE] SW Event: ${event}`, data);

    if (event === 'error') {
      this.metrics.serviceWorkerErrors++;
      
      const errorEvent: CacheEvent = {
        type: 'error',
        url: data.url || 'unknown',
        reason: data.message || 'Unknown error',
        timestamp: Date.now()
      };
      
      this.addEvent(errorEvent);
    }

    this.exportToPrometheus();
  }

  /**
   * Report quota exceeded event
   */
  reportQuotaExceeded(): void {
    console.warn('[CACHE] Storage quota exceeded');
    
    const event: CacheEvent = {
      type: 'error',
      url: 'quota',
      reason: 'Storage quota exceeded',
      timestamp: Date.now()
    };
    
    this.addEvent(event);
    this.metrics.serviceWorkerErrors++;

    this.exportToPrometheus();
  }

  /**
   * Track cache eviction
   */
  trackEviction(url: string, reason: string): void {
    const event: CacheEvent = {
      type: 'eviction',
      url,
      reason,
      timestamp: Date.now()
    };

    this.addEvent(event);
    // console.log(`[CACHE] Evicted: ${url} (${reason})`);
  }

  /**
   * Track preload event
   */
  trackPreload(url: string, loadTime: number, size?: number): void {
    const event: CacheEvent = {
      type: 'preload',
      url,
      loadTime,
      size,
      timestamp: Date.now()
    };

    this.addEvent(event);
    // console.log(`[CACHE] Preloaded: ${url} (${loadTime}ms)`);
  }

  /**
   * Get current metrics
   */
  getMetrics(): CacheMetrics {
    return { ...this.metrics };
  }

  /**
   * Get recent events
   */
  getEvents(limit = 100): CacheEvent[] {
    return this.events.slice(-limit);
  }

  /**
   * Get Prometheus-formatted metrics
   */
  getPrometheusMetrics(): PrometheusMetrics {
    return {
      cache_hits_total: this.metrics.cacheHits,
      cache_misses_total: this.metrics.cacheMisses,
      cache_requests_total: this.metrics.totalRequests,
      cache_hit_ratio: this.metrics.hitRate,
      cache_load_time_seconds: this.metrics.averageLoadTime / 1000,
      cache_size_bytes: this.metrics.totalCacheSize,
      cache_evictions_total: this.events.filter(e => e.type === 'eviction').length,
      service_worker_errors_total: this.metrics.serviceWorkerErrors
    };
  }

  /**
   * Reset all metrics
   */
  reset(): void {
    this.metrics = {
      hitRate: 0,
      missRate: 0,
      averageLoadTime: 0,
      totalCacheSize: 0,
      networkBytesSaved: 0,
      assetsServedFromCache: 0,
      totalRequests: 0,
      cacheHits: 0,
      cacheMisses: 0,
      serviceWorkerErrors: 0
    };
    this.events = [];
    // console.log('[CACHE] Metrics reset');
  }

  /**
   * Export metrics to Prometheus (if available)
   */
  private exportToPrometheus(): void {
    if (!this.prometheusEnabled) return;

    try {
      const metrics = this.getPrometheusMetrics();
      
      // Send metrics to prosemirror-server Prometheus endpoint
/*      await fetch('/api/metrics/cache', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(metrics)
      });*/
    } catch (error) {
      // console.warn('[CACHE] Failed to export Prometheus metrics:', error);
    }
  }

  /**
   * Check if Prometheus support is available
   */
  private checkPrometheusSupport(): void {
    // Check if we can reach the Prometheus endpoint
    this.prometheusEnabled = true;
/*    fetch('/api/metrics/cache', { method: 'HEAD' })
      .then(response => {
        if (response.ok) {
          this.prometheusEnabled = true;
          console.log('[CACHE] Prometheus metrics enabled');
        }
      })
      .catch(() => {
        // Prometheus not available, continue without it
        console.log('[CACHE] Prometheus metrics not available');
      });*/
  }

  /**
   * Add event to the events array
   */
  private addEvent(event: CacheEvent): void {
    this.events.push(event);
    
    // Keep only the last maxEvents
    if (this.events.length > this.maxEvents) {
      this.events = this.events.slice(-this.maxEvents);
    }
  }

  /**
   * Update hit/miss rates
   */
  private updateRates(): void {
    if (this.metrics.totalRequests > 0) {
      this.metrics.hitRate = (this.metrics.cacheHits / this.metrics.totalRequests) * 100;
      this.metrics.missRate = (this.metrics.cacheMisses / this.metrics.totalRequests) * 100;
    }
  }

  /**
   * Update average load time
   */
  private updateAverageLoadTime(loadTime: number): void {
    const totalLoadTime = this.metrics.averageLoadTime * (this.metrics.totalRequests - 1);
    this.metrics.averageLoadTime = (totalLoadTime + loadTime) / this.metrics.totalRequests;
  }
}

// Export singleton instance
export const cachePerformanceMonitor = new CachePerformanceMonitor();
export default cachePerformanceMonitor;